import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { GeofenceStyleFormModel } from '@casops/side-panels/manage-geofence-style/useManageGeofenceStyle';
import { DropdownItemProps } from '@cas/components/inputs/dropdown-items/DropdownItems';
import CasFormDropdown from '@cas/components/inputs/cas-form-dropdown/CasFormDropdown';
import CasFormInput from '@cas/components/inputs/cas-form-input/CasFormInput';
import styles from './FormBorderConfigSelector.module.scss';
import ColourSelector from '../../colour-selector/ColourSelector';
import Field from '@cas/components/inputs/field/Field';
import ColourButton from '../../colour-button/ColourButton';
import solidLineImageUrl from '@cas/assets/patterns/solidline.png';
import dotLineImageUrl from '@cas/assets/patterns/dotline.png';
import dashdotLineImageUrl from '@cas/assets/patterns/dashdotline.png';

const borderLineStyleOptionsData = [
  { key: 'Solid', label: 'Solid', value: 1, imageUrl: solidLineImageUrl },
  { key: 'Dotted', label: 'Dotted', value: 2, imageUrl: dotLineImageUrl },
  { key: 'Dash-Dot', label: 'Dash-Dot', value: 3, imageUrl: dashdotLineImageUrl },
] as const;

const borderWidthOptionsData = [
  { label: '1px', value: 1 },
  { label: '2px', value: 2 },
  { label: '3px', value: 3 },
  { label: '4px', value: 4 },
  { label: '5px', value: 5 },
  { label: '6px', value: 6 },
  { label: '7px', value: 7 },
  { label: '8px', value: 8 },
] as const;

const borderOpacityOptionsData = [
  { label: '0', value: 0 },
  { label: '10', value: 10 },
  { label: '20', value: 20 },
  { label: '30', value: 30 },
  { label: '40', value: 40 },
  { label: '50', value: 50 },
  { label: '60', value: 60 },
  { label: '70', value: 70 },
  { label: '80', value: 80 },
  { label: '90', value: 90 },
  { label: '100', value: 100 },
] as const;

export type BorderLineStyleKey = (typeof borderLineStyleOptionsData)[number]['key'];
export type BorderLineStyleValue = (typeof borderLineStyleOptionsData)[number]['value'];
export type BorderWidthValue = (typeof borderWidthOptionsData)[number]['value'];
export type BorderOpacityValue = (typeof borderOpacityOptionsData)[number]['value'];

const LINE_COLOUR = 'line.colour';
const LINE_WIDTH = 'line.width';
const LINE_PATTERN = 'line.pattern';
const LINE_OPACITY = 'line.opacity';

const borderLineStyleDropdownItems: DropdownItemProps<BorderLineStyleValue>[] =
  borderLineStyleOptionsData.map(({ key, label, value }) => ({
    key: label,
    value,
    stringValue: label,
    label: label,
  }));

const borderWidthDropdownItems: DropdownItemProps<BorderWidthValue>[] = borderWidthOptionsData.map(
  ({ label, value }) => ({
    key: label,
    value,
    stringValue: label,
    label,
  }),
);

const borderOpacityDropdownItems: DropdownItemProps<BorderOpacityValue>[] =
  borderOpacityOptionsData.map(({ label, value }) => ({
    key: label,
    value,
    stringValue: label,
    label,
  }));

const FormBorderConfigSelector = ({
  control,
  watch,
  setValue,
}: {
  control: UseFormReturn<GeofenceStyleFormModel>['control'];
  watch: UseFormReturn<GeofenceStyleFormModel>['watch'];
  setValue: UseFormReturn<GeofenceStyleFormModel>['setValue'];
}) => {
  const { t } = useTranslation();
  const watchLineColour = watch(LINE_COLOUR);
  const watchLineWidth = watch(LINE_WIDTH);
  const watchLinePattern = watch(LINE_PATTERN);
  const watchLineOpacity = watch(LINE_OPACITY);

  const patternImageUrl = useMemo(() => {
    const pattern = borderLineStyleOptionsData.find(p => p.value === watchLinePattern);
    return pattern?.imageUrl ?? '';
  }, [watchLinePattern]);

  const translatedLineStyleItems = useMemo(
    () =>
      borderLineStyleDropdownItems.map(item => ({
        ...item,
        label: t(`LineStyles.${item.key}`),
      })),
    [t],
  );

  const lineColourHandler = useCallback(
    (colour: string) => {
      setValue(LINE_COLOUR, colour, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [setValue],
  );

  return (
    <div className={styles.section}>
      <h3 className={styles.heading}>{t('BorderLine')}</h3>
      <CasFormDropdown
        control={control}
        name="line.pattern"
        label={t('Fields.LineStyle')}
        items={translatedLineStyleItems}
      />
      <div className={styles.secondaryOptions}>
        <div className={styles.colourInput}>
          <CasFormInput control={control} name={LINE_COLOUR} label={t('Fields.colour')} />
        </div>
        <div className={styles.colourSelector}>
          <ColourSelector
            colour={watchLineColour}
            label={t('Fields.colourSelector')}
            onChange={lineColourHandler}
          />
        </div>
        <div className={styles.lineWidth}>
          <CasFormDropdown
            control={control}
            name="line.width"
            label={t('Fields.Width')}
            items={borderWidthDropdownItems}
          />
        </div>
        <div className={styles.lineOpacity}>
          <CasFormDropdown
            control={control}
            name="line.opacity"
            label={t('Fields.Opacity')}
            items={borderOpacityDropdownItems}
          />
        </div>
      </div>
      <Field uid="line-preview" label={t('Fields.preview')}>
        <div className={styles.preview}>
          <ColourButton
            colour={watchLineColour}
            patternImageUrl={patternImageUrl}
            makeTransparent={!patternImageUrl}
            opacity={watchLineOpacity}
            height={watchLineWidth}
          />
        </div>
      </Field>
    </div>
  );
};

export default FormBorderConfigSelector;
